import * as React from 'react';
import type {
  Nullable,
  EntityRootData,
  EntityDetail,
  EnvironmentKey,
  DomainEntity,
  EntityDetailSchema,
} from '@web-config-app/core';
import type {
  OtherAuthoringEnvironmentState,
  UseEntityOperationsResult,
} from '@web-config-app/core-react';
import {
  useEntityDetails,
  UseEntityDetailsProps,
  UseEntityDetailsResult,
} from '../../hooks/use-entity-details/use-entity-details';

export type EntityDetailsContextType = UseEntityDetailsResult & {
  operations: UseEntityOperationsResult;
  currentAuthoringEnvironment: EnvironmentKey;
  otherAuthoringEnvironmentState?: OtherAuthoringEnvironmentState;
  entityRootData: EntityRootData | undefined;
  configEntitySchema: EntityDetailSchema;
  setEntityData: (data: any, path: string) => void;
  onTreeNodeSelect?: (args: any) => void;
  entityType: string;
  includedEntities: EntityDetail[] | undefined;
  addIncludedEntity?: (entity: EntityDetail) => void;
  setIncludedEntities?: (entities: EntityDetail[]) => void;
  resetEntityData: () => void;
  entity: DomainEntity;
  isUsingConfigAppBackend?: boolean;
};

export type EntityDetailsContextResult = Nullable<EntityDetailsContextType>;

export const EntityDetailsContext =
  React.createContext<EntityDetailsContextResult>(null);

export interface Included {}

export interface EntityDetailsProviderProps
  extends Omit<UseEntityDetailsProps, 'data' | 'onDataChange'> {
  operations: UseEntityOperationsResult;
  currentAuthoringEnvironment: EnvironmentKey;
  otherAuthoringEnvironmentState?: OtherAuthoringEnvironmentState;
  entityType: string;
  defaultEntityName?: string;
  includedEntities: EntityDetail[] | undefined;
  configEntitySchema: EntityDetailSchema;
  setIncludedEntities: (data: EntityDetail[] | undefined) => void;
  addIncludedEntity: (data: EntityDetail) => void;
  resetEntityData: () => void;
  isUsingConfigAppBackend?: boolean;
}

export const EntityDetailsProvider = ({
  children,
  operations,
  entityRootData,
  entityMeta,
  currentAuthoringEnvironment,
  otherAuthoringEnvironmentState,
  rootSchema,
  onTreeNodeSelect,
  setEntityData,
  path,
  entityType,
  instanceId,
  domainEntity: entity,
  includedEntities,
  setIncludedEntities,
  addIncludedEntity,
  resetEntityData,
  name,
  defaultEntityName,
  status,
  configEntitySchema,
  isUsingConfigAppBackend,
}: React.PropsWithChildren<EntityDetailsProviderProps>) => {
  const entityDetailsValue = useEntityDetails({
    name,
    status,
    path,
    instanceId,
    rootSchema,
    entityRootData,
    entityMeta,
    onTreeNodeSelect,
    setEntityData,
    domainEntity: entity,
    defaultEntityName,
  });

  const entityDetailsCtxValue = React.useMemo(
    () => ({
      ...entityDetailsValue,
      includedEntities,
      setIncludedEntities,
      addIncludedEntity,
      resetEntityData,
      entityType,
      operations,
      currentAuthoringEnvironment,
      otherAuthoringEnvironmentState,
      entityRootData,
      entity,
      configEntitySchema,
      isUsingConfigAppBackend,
    }),
    [
      entityDetailsValue,
      entityType,
      operations,
      configEntitySchema,
      currentAuthoringEnvironment,
      otherAuthoringEnvironmentState,
      entityRootData,
      includedEntities,
      setIncludedEntities,
      addIncludedEntity,
      resetEntityData,
      entity,
      isUsingConfigAppBackend,
    ],
  );

  return (
    <EntityDetailsContext.Provider value={entityDetailsCtxValue}>
      {children}
    </EntityDetailsContext.Provider>
  );
};
